import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { NavBar, Footer } from '../../components/redesign/shared.jsx';
import SEO from '../../components/SEO.jsx';
import { useCasesData } from '../../data/useCasesData.js';

const INDUSTRY_NUM = { healthcare: 'I.', logistics: 'II.', 'financial-services': 'III.', 'carbon-credit-registry': 'IV.' };

export function UseCasesListA({ onNav }) {
  return (
    <div className="pageA">
      <SEO
        title="Use Cases — CRMs built for regulated industries | Aakeeo"
        description="How healthcare networks, logistics operators, financial services firms and carbon credit registries ship SuiteCRM on their own infrastructure. Real outcomes, real architectures."
        keywords={['SuiteCRM healthcare', 'SuiteCRM logistics', 'SuiteCRM financial services', 'SuiteCRM carbon registry', 'self-hosted CRM']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Use Cases' },
        ]}
      />
      <NavBar page="cases" onNav={onNav} />

      <main>

      <section className="solA-hero">
        <span className="eyebrowA">Use Cases</span>
        <h1>
          Proven across industries where&nbsp;<em>data ownership</em>&nbsp;isn&apos;t negotiable.
        </h1>
        <p className="solA-hero-sub">
          We don&apos;t ship demo tenants or slide decks. These are real engagements — healthcare networks, freight brokers, wealth managers, carbon credit registries — running SuiteCRM on their own infrastructure, at their own scale.
        </p>
      </section>

      <section className="ucA-list">
        <div className="ucA-list-head">
          <span className="eyebrowA">Index</span>
          <h2>Four industries. One pattern.</h2>
        </div>
        <div className="ucA-cards">
          {useCasesData.map((u) => (
            <Link key={u.slug} to={`/use-cases/${u.slug}`} className="ucA-card">
              <div className="ucA-card-num">{INDUSTRY_NUM[u.slug] || '—'}</div>
              <div className="ucA-card-body">
                <span className="ucA-card-industry">{u.industrySector}</span>
                <h3>{u.heroHeadline}</h3>
                <p>{u.painPointLead}</p>
                <div className="ucA-card-stats">
                  {u.resultDetails.map((r) => (
                    <div key={r.label}>
                      <strong>{r.value}</strong>
                      <span>{r.label}</span>
                    </div>
                  ))}
                </div>
                <span className="ucA-card-read">Read the full case →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="ctaA">
        <div className="ctaA-inner">
          <h2>Your industry isn&apos;t on the list?</h2>
          <p>We&apos;ve shipped in ed-tech, non-profits, SaaS, and manufacturing too. 30-minute call to see whether the pattern fits.</p>
          <div className="ctaA-buttons">
            <button className="btnA-primary" onClick={() => onNav('contact')}>Book a strategy call</button>
            <button className="btnA-ghost" onClick={() => onNav('solutions')}>See Solutions →</button>
          </div>
        </div>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}

export function UseCaseDetailA({ onNav }) {
  const { slug } = useParams();
  const useCase = useCasesData.find((u) => u.slug === slug);

  if (!useCase) {
    return (
      <div className="pageA">
        <NavBar page="cases" onNav={onNav} />
        <main>
          <section className="solA-hero">
            <span className="eyebrowA">Not found</span>
            <h1>That case study doesn&apos;t exist.</h1>
            <div className="solA-hero-cta">
              <Link to="/use-cases" className="btnA-primary">← All use cases</Link>
            </div>
          </section>
        </main>
        <Footer onNav={onNav} />
      </div>
    );
  }

  const num = INDUSTRY_NUM[useCase.slug] || '—';

  return (
    <div className="pageA">
      <SEO
        title={`${useCase.industrySector} — Use Case | Aakeeo`}
        description={useCase.metaDescription || useCase.painPointLead}
        keywords={[useCase.industrySector, 'SuiteCRM use case', 'self-hosted CRM']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Use Cases', url: '/use-cases' },
          { name: useCase.industrySector },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: useCase.heroHeadline,
          description: useCase.painPointLead,
          about: useCase.industrySector,
          author: { '@type': 'Organization', name: 'Aakeeo Technologies', url: 'https://www.aakeeo.com' },
          publisher: {
            '@type': 'Organization',
            name: 'Aakeeo Technologies',
            logo: { '@type': 'ImageObject', url: 'https://www.aakeeo.com/logo.png' },
          },
        }}
      />
      <NavBar page="cases" onNav={onNav} />

      <main>

      {/* HERO */}
      <section className="ucDA-hero">
        <Link to="/use-cases" className="ucDA-back">← All use cases</Link>
        <div className="ucDA-hero-inner">
          <div className="ucDA-hero-left">
            <span className="ucDA-num">{num}</span>
            <span className="ucDA-tag">{useCase.industrySector}</span>
          </div>
          <div className="ucDA-hero-right">
            <h1>{useCase.heroHeadline}</h1>
            <p>{useCase.painPointLead}</p>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="ucDA-results">
        <div className="ucDA-results-head">
          <span className="eyebrowA">The outcome</span>
          <h2>{useCase.roiStat}</h2>
        </div>
        <div className="ucDA-stats">
          {useCase.resultDetails.map((r) => (
            <div key={r.label} className="ucDA-stat">
              <strong>{r.value}</strong>
              <span className="ucDA-stat-label">{r.label}</span>
              <p>{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="ucDA-problem">
        <div className="ucDA-problem-head">
          <span className="eyebrowA">01 — The problem</span>
          <h2>Where the system was breaking.</h2>
        </div>
        <ol className="ucDA-problem-list">
          {useCase.theProblem.map((p, i) => (
            <li key={i}>
              <span className="ucDA-problem-num">{String(i + 1).padStart(2, '0')}</span>
              <p>{p}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* SOLUTION */}
      <section className="ucDA-solution">
        <div className="ucDA-solution-copy">
          <span className="eyebrowA">02 — What we built</span>
          <h2>The architecture</h2>
          <p>{useCase.architectureSolution}</p>
        </div>
        <div className="ucDA-solution-features">
          <span className="ucDA-solution-label">Includes</span>
          <ul>
            {useCase.features.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* RELATED READING */}
      {useCase.relatedBlog && (
        <section className="ucDA-related">
          <div className="ucDA-related-head">
            <span className="eyebrowA">Related reading</span>
            <h2>Go deeper on the architecture.</h2>
          </div>
          <Link to={useCase.relatedBlog.slug} className="ucDA-related-card">
            <span className="ucDA-related-tag">Technical guide</span>
            <h3>{useCase.relatedBlog.title}</h3>
            <p>{useCase.relatedBlog.excerpt}</p>
            <span className="ucDA-related-read">Read the article →</span>
          </Link>
        </section>
      )}

      {/* CTA */}
      <section className="ctaA">
        <div className="ctaA-inner">
          <h2>Same pattern. Different industry?</h2>
          <p>We can tell you in 30 minutes whether it fits yours. No slide deck, no obligation.</p>
          <div className="ctaA-buttons">
            <button className="btnA-primary" onClick={() => onNav('contact')}>Book a strategy call</button>
            <Link to="/use-cases" className="btnA-ghost">See other cases →</Link>
          </div>
        </div>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}
